import { useEffect } from "react";
import { isEsPath, localizedPath } from "@/lib/language";

const SITE_URL = "https://egconstructionsd.com";
const DEFAULT_OG_IMAGE = "https://egconstructionsd.com/images/sunny/aerial-wide.jpg";

interface SEOAlternate {
  hreflang: string;
  href: string;
}

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  lang?: "en" | "es";
  type?: "website" | "article";
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  alternates?: SEOAlternate[];
  noindex?: boolean;
  /** When noindex is set, still allow link following (ad landing pages). */
  follow?: boolean;
}


function setMeta(name: string, content: string, attr: "name" | "property" = "name") {
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function useSEO({
  title,
  description,
  canonical,
  ogImage,
  lang = "en",
  type = "website",
  jsonLd,
  alternates,
  noindex = false,
  follow = false,
}: SEOProps) {
  useEffect(() => {
    // Title
    document.title = title;

    // Description
    setMeta("description", description);

    // Robots
    setMeta("robots", noindex ? (follow ? "noindex, follow" : "noindex, nofollow") : "index, follow");


    // Canonical — language aware. The /es tree canonicalizes to itself.
    const currentPath = typeof window !== "undefined" ? window.location.pathname : "/";
    const isEs = isEsPath(currentPath);
    const rawPath = canonical ? new URL(canonical, SITE_URL).pathname : currentPath;
    const enUrl = `${SITE_URL}${localizedPath(rawPath, "en")}`;
    const esUrl = `${SITE_URL}${localizedPath(rawPath, "es")}`;
    const canonicalUrl = isEs ? esUrl : enUrl;
    setLink("canonical", canonicalUrl);

    // hreflang alternates (defaults to the EN/ES pair for this page)
    document.querySelectorAll('link[rel="alternate"][data-seo-hreflang]').forEach((el) => el.remove());
    const alts: SEOAlternate[] =
      alternates
        ? alternates
        : [
            { hreflang: "en", href: enUrl },
            { hreflang: "es", href: esUrl },
            { hreflang: "x-default", href: enUrl },
          ];
    alts.forEach((alt) => {
      const link = document.createElement("link");
      link.setAttribute("rel", "alternate");
      link.setAttribute("hreflang", alt.hreflang);
      link.setAttribute("href", alt.href);
      link.setAttribute("data-seo-hreflang", "true");
      document.head.appendChild(link);
    });

    // Open Graph
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:type", type, "property");
    setMeta("og:url", canonicalUrl, "property");
    setMeta("og:image", ogImage || DEFAULT_OG_IMAGE, "property");
    setMeta("og:locale", lang === "en" ? "en_US" : "es_US", "property");
    setMeta("og:site_name", "EG Construction", "property");

    // Twitter
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setMeta("twitter:image", ogImage || DEFAULT_OG_IMAGE);

    // Language
    document.documentElement.lang = lang;

    // JSON-LD
    let script = document.getElementById("page-jsonld") as HTMLScriptElement | null;
    if (jsonLd) {
      if (!script) {
        script = document.createElement("script");
        script.id = "page-jsonld";
        script.setAttribute("type", "application/ld+json");
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(Array.isArray(jsonLd) ? jsonLd : jsonLd);
    }

    return () => {
      script?.remove();
      document.querySelectorAll('link[rel="alternate"][data-seo-hreflang]').forEach((el) => el.remove());
    };
  }, [title, description, canonical, ogImage, lang, type, jsonLd, alternates, noindex, follow]);
}
